import { CurrencyPipe, DatePipe, DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { NotLoggedService } from 'src/app/common/not-logged/not-logged.service';
import { getDefaultModalSettings } from 'src/app/common/utils/helpers';
import { SharryContentType, SharrySelection, UserRole } from 'src/types/graphql';
import { datesAreOnSameDay } from 'utils/date-utils';
import { AvatarDirective } from '../../../common/avatar/avatar.directive';
import { BaseComponent } from '../base/base.component';
import { EllipsisTooltipDirective } from '../../../common/ellipsis-tooltip/ellipsis-tooltip.directive';
import {
  CardContentEventFragment,
  CardContentSharryFragment,
  CardContentWonderFragment,
} from '../../../common/fragments/card-content.fragment.generated';
import { CardUserFragment } from '../../../common/fragments/card-user.fragment.generated';
import { GtagService } from '../../../common/gtag/gtag.service';
import { OpenDialogService } from '../../../common/open-dialog/open-dialog.service';
import { TypedTemplateDirective } from '../../../common/typed-template/typed-template.directive';
import { UserNamePipe } from '../../../common/user-name/user-name.pipe';
import { VariantDatePipe } from '../../../common/variant/variant-date/variant-date.pipe';
import { GetSharryContentCardBookmarkedGQL, GetUserCardBookmarkedGQL } from './content-card.query.generated';
import { SlugPipe } from '../../pipes/slug.pipe';
import { ContentTypeColorizerPipe } from '../../pipes/content-type-colorizer.pipe';
import { map } from 'rxjs';

@Component({
  selector: 'sharry-content-card',
  standalone: true,
  imports: [
    NgClass,
    DecimalPipe,
    DatePipe,
    CurrencyPipe,
    NgTemplateOutlet,
    RouterLink,
    MatButtonModule,
    SlugPipe,
    I18NextModule,
    MatDialogModule,
    UserNamePipe,
    AvatarDirective,
    IconComponent,
    EllipsisTooltipDirective,
    ContentTypeColorizerPipe,
    MatButtonModule,
    TypedTemplateDirective,
    VariantDatePipe,
  ],
  templateUrl: './content-card.component.html',
  styleUrls: ['./content-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentCardComponent extends BaseComponent {
  card: InputSignal<any> = input.required<any>();
  classes: InputSignal<string> = input<string>('');
  modalClosed: WritableSignal<boolean> = signal<boolean>(false);
  bookmarkedChanged: OutputEmitterRef<any> = output<any>();

  cardContentToken: { $implicit: CardContentEventFragment | CardContentSharryFragment | CardContentWonderFragment };
  cardSharryToken: { $implicit: CardContentSharryFragment };
  cardEventToken: { $implicit: CardContentEventFragment };
  cardWonderToken: { $implicit: CardContentWonderFragment };
  cardUserToken: { $implicit: CardUserFragment };
  sharrySelection: typeof SharrySelection = SharrySelection;

  datesAreOnSameDay = datesAreOnSameDay;

  constructor(
    private notLoggedService: NotLoggedService,
    private openDialogService: OpenDialogService,
    private getSharryContentCardBookmarkedGQL: GetSharryContentCardBookmarkedGQL,
    private getUserCardBookmarkedGQL: GetUserCardBookmarkedGQL,
    private gtagService: GtagService,
  ) {
    super();

    effect(() => {
      if (this.modalClosed()) {
        if (this.userCard()) {
          this.fetchUserContentCardBookmarked();
        } else {
          this.fetchSharryContentCardBookmarked();
        }
      }
    });
  }

  userCard: Signal<boolean> = computed((): boolean => this.card()?.__typename === 'User');

  bookmarked: Signal<boolean> = computed((): boolean => this.card()?.bookmarked);

  freeSharryKind: Signal<string> = computed((): string => this.card()?.freeSharryKind);

  durationType: Signal<string> = computed((): string => this.card()?.variantDuration?.durationType);

  lowerCaseTypeName: Signal<string> = computed((): string => this.card()?.__typename?.toLowerCase());

  distanceBiggerThanOne: Signal<boolean> = computed((): boolean => +this.card()?.distance > 1);

  trustedUser: Signal<boolean> = computed((): boolean => this.card()?.role === UserRole.TrustedUser);

  isSharryContent: Signal<boolean> = computed((): boolean => this.card()?.contentType === SharryContentType.Sharry);

  imgSrc: Signal<string> = computed((): string => {
    const card = this.card();
    const contentType: SharryContentType = this.isSharryContent() ? card?.sharrySelection : card?.contentType;
    const soldBySharryLand: string = card?.soldBySharryland ? '--sold-by-sharryland' : '';
    return `https://media.sharry.land/app/images/families/ic-${contentType}-card${soldBySharryLand}.webp`;
  });

  address: Signal<string> = computed((): string => {
    const card = this.card();
    return this.userCard() ? card.workAddress : card.shortAddress;
  });

  formattedaddress: Signal<string> = computed((): string => {
    const addressValue = this.address();
    return addressValue && this.distanceBiggerThanOne() ? ' | ' + addressValue : addressValue;
  });

  finishedEvent: Signal<boolean> = computed((): boolean => {
    const nextDate = new Date(this.card().nextUpcomingInstanceDate);
    return nextDate < new Date();
  });

  nonRecorsiveEvent: Signal<boolean> = computed((): boolean => {
    const nextEventInstance = this.card().nextEventInstance;
    return nextEventInstance?.hasDates && !this.datesAreOnSameDay(nextEventInstance.beginsAt, nextEventInstance.endsAt);
  });

  async onLike(card: any) {
    const { ManageLikesComponent } = await import('../../dialogs/manage-likes/manage-likes.component');

    const handleLikeDialog = () => {
      this.openDialogService
        .openDialog<any, { content?: any; currentLikeFolderId?: string }>(ManageLikesComponent, {
          ...getDefaultModalSettings(),
          data: {
            content: { ...card },
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.modalClosed.set(true);
        });
    };
    this.notLoggedService.execute(() => handleLikeDialog());
  }

  fetchSharryContentCardBookmarked() {
    this.getSharryContentCardBookmarkedGQL
      .fetch({ id: this.card().id }, { fetchPolicy: 'no-cache' })
      .pipe(map(r => r.data.sharryContent.bookmarked))
      .subscribe((bookmarked: any) => {
        this.bookmarkedChanged.emit({ ...this.card(), bookmarked });
        this.modalClosed.set(false);
      });
  }

  fetchUserContentCardBookmarked() {
    this.getUserCardBookmarkedGQL
      .fetch({ id: this.card().id }, { fetchPolicy: 'no-cache' })
      .pipe(map(r => r.data.user.bookmarked))
      .subscribe((bookmarked: any) => {
        this.bookmarkedChanged.emit({ ...this.card(), bookmarked });
        this.modalClosed.set(false);
      });
  }

  triggerViewItem(card: any) {
    const { title, id, contentType, owner, sharryKind, sharrySelection, nextUpcomingVariant } = card;
    const price = contentType === SharryContentType.Sharry ? nextUpcomingVariant?.discountedPrice : undefined;
    const itemBrand = contentType === SharryContentType.Wonder ? SharryContentType.Wonder : owner?.businessName;
    const itemCategory = sharryKind?.name;
    const trackingEvent = {
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: title,
            item_id: id,
            price,
            item_brand: itemBrand,
            item_category: itemCategory,
            item_variant: sharrySelection,
          },
        ],
      },
    };
    this.gtagService.pushEvent(trackingEvent, true);
  }
}
